import { Annotation, AnnotationTemplate, AnnotationTemplateType } from '@paragon/contract-library';
import { sealingService } from '@paragon/data-connections';
import { getLoggedInUser } from '@paragon/login-tools';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { duplicateAnnotationWithNewGuid } from '../modules/annotations/annotationUtilities';
import {
  annotationTemplateAdded,
  annotationTemplatesLoaded,
  deleteAnnotationTemplateFailed,
  deleteAnnotationTemplateSucceeded,
  editAnnotationTemplate,
  markTemplatesAsSaved,
  selectAnnotationTemplate,
} from '../store/annotationTemplates';
import { AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';

export function loadAnnotationTemplates(): AsyncThunkActionVoid {
  return async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage('Loading templates...'));

    try {
      const loadedTemplates = await sealingService.annotationTemplates.fetchAllForUser();
      dispatch(annotationTemplatesLoaded(loadedTemplates));
    } catch (err) {
      console.log(`Unable to load annotation templates: ${(err as Error).message}`);
    }
    dispatch(removeSpinnerMessage(spinnerId));
  };
}

export function addAnnotationTemplate(
  name: string,
  group: string | null,
  selectedAnnotations: readonly Annotation[],
  isOrganizationTemplate: boolean,
  templateType: AnnotationTemplateType,
): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Saving templates...'));

    const selectedAnnotationsWithNewGuids = selectedAnnotations.map(duplicateAnnotationWithNewGuid);
    const user = getLoggedInUser(getState());
    if (!user) throw new Error('No logged in user found.');
    const annotationTemplate: AnnotationTemplate = {
      id: 0,
      name,
      contentsAsAnnotations: selectedAnnotationsWithNewGuids,
      group,
      userEmail: isOrganizationTemplate ? null : user.email,
      organization: isOrganizationTemplate ? user.organization : null,
      templateType,
    };

    const newTemplate = await sealingService.annotationTemplates.insert(annotationTemplate);
    dispatch(annotationTemplateAdded(newTemplate));
    dispatch(selectAnnotationTemplate(newTemplate.id));
    dispatch(removeSpinnerMessage(spinnerId));
  };
}

export function editTemplateAnnotations(templateId: number, selectedAnnotations: Annotation[]): ThunkActionVoid {
  return (dispatch) => {
    const selectedAnnotationsWithNewGuids = selectedAnnotations.map(duplicateAnnotationWithNewGuid);
    dispatch(
      editAnnotationTemplate({
        templateId,
        editedProperties: { contentsAsAnnotations: selectedAnnotationsWithNewGuids },
      }),
    );
  };
}

export function renameTemplate(templateId: number, newName: string) {
  return editAnnotationTemplate({ templateId, editedProperties: { name: newName } });
}

export function saveModifiedTemplates(): AsyncThunkActionVoid {
  return async (dispatch, getState) => {
    const spinnerId = dispatch(addSpinnerMessage('Saving templates...'));

    const runningPromises: Promise<void | AnnotationTemplate>[] = [];

    const state = getState();
    const annotationTemplatesState = state.annotationTemplates;

    for (const modifiedTemplateId of annotationTemplatesState.modifiedTemplates) {
      const modifiedAnnotationTemplate = annotationTemplatesState.annotationTemplates[modifiedTemplateId];
      runningPromises.push(sealingService.annotationTemplates.update(modifiedAnnotationTemplate));
    }

    for (const deletedTemplateId of annotationTemplatesState.deletedTemplates) {
      runningPromises.push(
        sealingService.annotationTemplates
          .remove(deletedTemplateId)
          .catch((err) => {
            dispatch(deleteAnnotationTemplateFailed(deletedTemplateId));

            console.error(err);
            dispatch(enqueueError('Error while deleting a template. Please try again.'));
          })
          .then(() => {
            dispatch(deleteAnnotationTemplateSucceeded(deletedTemplateId));
          }),
      );
    }

    try {
      await Promise.all(runningPromises);
      dispatch(markTemplatesAsSaved());
    } catch (err_1) {
      dispatch(
        enqueueError(
          'Error while saving templates. Please check your internet connection and try again. Contact customer support if issue persists.',
        ),
      );
      console.error(err_1);
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}
